import React from 'react';
import Button from 'components/ui/Button';
import { ExternalLink, Copy, BarChart3, Calendar } from 'lucide-react';

const GeneratedLinksPanel = ({ recentLinks, onCreateNew }) => {
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard?.writeText(text);
      // Could add toast notification here
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Actif';
      case 'paused':
        return 'En pause';
      case 'expired':
        return 'Expiré';
      case 'draft':
        return 'Brouillon';
      default:
        return status;
    }
  };

  if (!recentLinks || recentLinks?.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center py-8">
          <ExternalLink className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            Aucun lien créé
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            Commencez par créer votre premier lien de suivi pour voir les résultats ici
          </p>
          <Button 
            onClick={onCreateNew}
            className="mt-4"
          >
            Créer un lien
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-medium text-gray-900">
          Liens récents
        </h3>
        <Button
          variant="outline"
          size="sm"
          onClick={() => window.location.href = '/link-analytics'}
        >
          <BarChart3 className="h-4 w-4 mr-2" />
          Voir toutes les analyses
        </Button>
      </div>
      <div className="space-y-4">
        {recentLinks?.map((link) => (
          <div
            key={link?.id}
            className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-gray-900 truncate">
                  {link?.title}
                </h4>
                {link?.description && (
                  <p className="text-xs text-gray-500 mt-1">
                    {link?.description}
                  </p>
                )}
                
                <div className="flex items-center space-x-4 mt-2">
                  <div className="flex items-center text-xs text-gray-500">
                    <Calendar className="h-3 w-3 mr-1" />
                    {formatDate(link?.created_at)}
                  </div>
                  {link?.campaigns && (
                    <span className="text-xs text-indigo-600">
                      {link?.campaigns?.name}
                    </span>
                  )}
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(link?.status)}`}>
                    {getStatusText(link?.status)}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <span>{link?.total_clicks || 0} clics</span>
                <span>{link?.unique_clicks || 0} uniques</span>
              </div>
              
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(link?.short_url || '')}
                >
                  <Copy className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(link?.short_url, '_blank')}
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="mt-3 p-2 bg-gray-50 rounded-md">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">Lien court:</span>
                <span className="text-xs font-mono text-gray-700 truncate ml-2 max-w-xs">
                  {link?.short_url}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 pt-4 border-t border-gray-200">
        <Button
          variant="outline"
          onClick={onCreateNew}
          className="w-full"
        >
          Créer un nouveau lien
        </Button>
      </div>
    </div>
  );
};

export default GeneratedLinksPanel;